import React, { useCallback, useMemo } from "react";

import { GanttProps } from "components/ComponentType";
import { IProductTemplate, ITableDataEntry } from "components/dashboard/product-template-table/utils/interfaces";
import { Config, Data, Layout, PlotData, Shape } from "plotly.js";
import Plot from "react-plotly.js";
import { T } from "services/lang/LanguageService";
import { formatDate, formatDateDuration } from "utils/time-utils";
import copyChartToClipBoard from "../../chartUtils";
import { clipboardIcon } from "../../icons";

type GanttChartProps = GanttProps & {
  templateData: ITableDataEntry[],
  configData: IProductTemplate | undefined,
}
const OFFSET_HIGH = 0.4;
const RECORD_LIMIT = 30;

const GanttChart: React.FC<GanttChartProps> = ({templateData, configData, ...props}) => {
  const getAdditionalInfo = useCallback((tableData: ITableDataEntry) => {
    return Object.values(tableData.entries).reduce((arr, val) => {
      if (val.type !== "INSTANT") {
        return `${arr}<br>${configData?.data?.attributes.find(a => a.referenceName === val.referenceName)?.displayName}: ${val.value}`;
      }
      return arr;
    }, "");
  }, [configData?.data?.attributes]);

  const ranges: Partial<PlotData>[] = useMemo(
    () =>
      templateData.reduce((array, acc, index) => {
        props.ganttRanges.forEach(gr => {
          const startDate = acc.entries[gr.startDateColumn!]?.value?.toString();
          const endDate = acc.entries[gr.endDateColumn!]?.value?.toString();
          const trace: Partial<PlotData> = {
            name: gr.title ?? "",
            x: [startDate, endDate],
            y: [index, index],
            marker: { color: gr.color, size: 0.01 },
            showlegend: false,
            text:
              `${gr.title}<br>` +
              `${T("layout.start")}: ${formatDate(startDate)}<br>` +
              `${T("layout.end")}: ${formatDate(endDate)}<br>` +
              `${T("word.duration")}: ${formatDateDuration(startDate, endDate)}` +
              `${configData && getAdditionalInfo(acc)}`,
            hoverinfo: "text",
          };
          array.push(trace);
        });
        return array;
      }, [] as Partial<PlotData>[]),
    [configData, getAdditionalInfo, props.ganttRanges, templateData]
  );

  const timestamps: Partial<PlotData>[] = useMemo(
    () =>
      templateData.reduce((array, acc, index) => {
        props.ganttTimestamps.forEach(gt => {
          const date = acc.entries[gt.dateColumn!]?.value?.toString();
          const trace: Partial<PlotData> = {
            name: gt.title ?? "",
            x: [date, date],
            y: [index, index],
            marker: { color: gt.color, symbol: gt.icon ?? "circle", size: 10 },
            showlegend: false,
            text:
              `${gt.title}<br>` +
              `${T("word.date")}: ${formatDate(date)}` +
              `${configData && getAdditionalInfo(acc)}`,
            hoverinfo: "text",
          };
          array.push(trace);
        });
        return array;
      }, [] as Partial<PlotData>[]),
    [configData, getAdditionalInfo, props.ganttTimestamps, templateData]
  );

  const yLabels = useMemo(
    () =>
      templateData
        ?.sort((a, b) => a.dateCreated.localeCompare(b.dateCreated))
        .map(
          td =>
            td.entries[props.columnOverrideYLabels]?.value?.toString() ??
            td.stringRepresentation ??
            "-"
        ),
    [props.columnOverrideYLabels, templateData]
  );

  const data: Data[] = [...ranges, ...timestamps];
  const layout: Partial<Layout> = useMemo(
    () => ({
      width: props.width,
      xaxis: {
        type: "date",
        showgrid: true,
        zeroline: false,
      },
      yaxis: {
        range: [-1, Math.min(templateData.length, RECORD_LIMIT + OFFSET_HIGH)],
        showgrid: true,
        ticktext: yLabels,
        tickvals: templateData.map((_td, i) => i),
        zeroline: false,
        autorange: false,
      },
      height: props.height ?? 500,
      shapes: ranges.map(tr => {
        return {
          x0: tr.x![0],
          x1: tr.x![1],
          y0: +tr.y![0]! - OFFSET_HIGH,
          y1: +tr.y![0]! + OFFSET_HIGH,
          line: { width: 0 },
          type: "rect",
          xref: "x",
          yref: "y",
          opacity: 1,
          fillcolor: props.ganttRanges.find(gr => gr.title === tr.name)?.color,
        };
      }) as Partial<Shape>[],
      hovermode: "closest",
      showlegend: false,
      legend: { orientation: "h" },
      margin: {
        l: Math.max(...yLabels.map(y => y.length)) * 7,
        r: 0,
        b: 20,
        t: 20,
      },
    }),
    [props.ganttRanges, props.height, props.width, ranges, templateData, yLabels]
  );

  const chartConfig: Partial<Config> = {
    displaylogo: false,
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    modeBarButtonsToAdd: [
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
    ],
  };

  return <Plot data={data} layout={layout} config={chartConfig} />;
};

export default GanttChart;
